import { establishedProfessionIds, professionCatalogue } from './catalogue.js'
import { categoryContent, categorySeoSummaries, professionPageHeading, professionWorkNounFor, usesPracticeManagementLanguage } from './categoryContent.js'
import { getProfessionHeroImage } from './professionImages.js'
import { pluralProfessionName, pluralProfessionNameAtStart } from './professionCopy.js'

const siteUrl = 'https://www.offlico.com'
const indexImage = '/public-assets/all-mobile-businesses/main-scenes/offlico-all-mobile-businesses-hero-professional.webp'

export const PROFESSION_CHILD_SLUGS = Object.freeze([
  'admin-overload',
  'lead-generation',
  'online-booking',
  'appointment-reminders',
  'client-records',
  'practice-management-software',
])

const childSeo = {
  'admin-overload': {
    title: 'Admin overload',
    heading: (_plural, profession) => `${pluralProfessionNameAtStart(profession.name)}: spending too much time on admin?`,
    intent: 'admin overload',
    intro: () => 'See how Offlico keeps bookings and everyday follow-up together.',
  },
  'lead-generation': {
    title: 'Managing enquiries',
    heading: (plural) => `Suitable enquiries for ${plural}`,
    intent: 'own-channel enquiry management',
    intro: () => 'Offlico local customer requests are not available for this role; use your own channels.',
  },
  'online-booking': {
    title: 'Online booking',
    heading: (plural) => `Online booking for ${plural}`,
    intent: 'online booking software',
    intro: () => 'Let people request a booking while you work, with service and location details ready for review.',
  },
  'appointment-reminders': {
    title: 'Appointment reminders',
    heading: (plural) => `Booking reminders for ${plural}`,
    intent: 'appointment reminder software',
    intro: () => 'Send timely reminders before scheduled work, with booking and contact details together.',
  },
  'client-records': {
    title: 'Client records',
    heading: (plural) => `Client records for ${plural}`,
    intent: 'client record software',
    intro: () => 'Find contacts, booking history, ordinary business notes and payment details when needed.',
  },
  'practice-management-software': {
    title: 'Business software',
    heading: (plural) => `Business management for ${plural}`,
    intent: 'business management software',
    intro: (work) => `Connect ${work}, contacts, reminders, invoices and payments in one business workflow.`,
  },
}

export const PROFESSION_INDEX_ROUTE_META = {
  path: '/for-businesses/professions',
  title: 'Software for mobile professions UK',
  description: 'Explore how Offlico fits mobile and on-site professions across health, home services, trades, events and more. Find business guidance for your work.',
  pageHeading: 'Find Offlico for the work you do',
  pageIntro: 'Browse Offlico guidance by profession and see where its business tools fit your working day.',
  searchIntent: 'software for mobile professions',
  image: indexImage,
  imageAlt: 'Mobile professional seated outside a home with a phone',
  jsonLd: [{
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'Professions', item: `${siteUrl}/professions/` },
    ],
  }],
}

const newProfessions = professionCatalogue.filter((profession) => !establishedProfessionIds.has(profession.id))

function completeDescription(base, categoryDetail) {
  if (base.length > 160) throw new Error(`Profession SEO description is too long: ${base}`)
  return categoryDetail && `${base} ${categoryDetail}`.length <= 160
    ? `${base} ${categoryDetail}`
    : base
}

function professionTitle(core) {
  // The shared SEO formatter appends " | Offlico" to the rendered title.
  // Keep the optional location cue within that final title budget.
  return core.length + 3 + ' | Offlico'.length <= 70 ? `${core} UK` : core
}

function breadcrumb(profession, suffix, title) {
  const items = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
    { '@type': 'ListItem', position: 2, name: profession.name, item: `${siteUrl}${profession.canonicalHref}` },
  ]
  if (suffix) items.push({ '@type': 'ListItem', position: 3, name: title, item: `${siteUrl}${profession.canonicalHref}${suffix}/` })
  return [{ '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items }]
}

function imageFor(profession, suffix = '') {
  const image = getProfessionHeroImage(profession)
  const sceneKind = {
    'admin-overload': 'admin-hero',
    'lead-generation': 'leads-hero',
    'online-booking': 'booking-hero',
    'appointment-reminders': 'reminders-hero',
    'client-records': 'records-hero',
    'practice-management-software': 'records-hero',
  }[suffix]
  return (sceneKind && image?.scenes?.[sceneKind]) || image || { src: indexImage, alt: 'Mobile professional at work' }
}

function hubMeta(profession) {
  const context = categoryContent[profession.categorySlug]
  if (!context) throw new Error(`Missing category copy for ${profession.categorySlug}`)
  const summary = categorySeoSummaries[profession.categorySlug] || context.summary
  const work = professionWorkNounFor(profession)
  const image = imageFor(profession)
  return {
    path: profession.internalHref.replace(/\/$/, ''),
    title: professionTitle(`Business admin software for ${pluralProfessionName(profession.name)}`),
    description: completeDescription(`Offlico for ${pluralProfessionName(profession.name)}: ${work}, contacts, reminders and business admin.`, summary),
    pageHeading: professionPageHeading(profession),
    pageIntro: context.intro,
    searchIntent: `${profession.name.toLowerCase()} business software`,
    image: image.src,
    imageAlt: image.alt,
    profession,
    featureSlug: '',
    jsonLd: breadcrumb(profession, '', profession.name),
  }
}

function childMeta(profession, suffix) {
  const child = suffix === 'practice-management-software' && usesPracticeManagementLanguage(profession)
    ? {
      ...childSeo[suffix],
      title: 'Practice management software',
      heading: (plural) => `Practice management for ${plural}`,
      intent: 'practice management software',
    }
    : childSeo[suffix]
  const context = categoryContent[profession.categorySlug]
  const work = professionWorkNounFor(profession)
  const image = imageFor(profession, suffix)
  const title = professionTitle(`${child.title} for ${pluralProfessionName(profession.name)}`)
  const intro = child.intro(work)
  return {
    path: `${profession.internalHref.replace(/\/$/, '')}/${suffix}`,
    title,
    description: completeDescription(`${child.title} for ${pluralProfessionName(profession.name)}. ${intro}`, categorySeoSummaries[profession.categorySlug] || context.summary),
    pageHeading: child.heading(pluralProfessionName(profession.name), profession),
    pageIntro: intro,
    searchIntent: `${profession.name.toLowerCase()} ${child.intent}`,
    image: image.src,
    imageAlt: image.alt,
    profession,
    featureSlug: suffix,
    jsonLd: breadcrumb(profession, suffix, child.title),
  }
}

export const PROFESSION_ROUTE_META = newProfessions.map(hubMeta)
export const PROFESSION_CHILD_ROUTE_META = newProfessions.flatMap((profession) =>
  PROFESSION_CHILD_SLUGS.map((suffix) => childMeta(profession, suffix)),
)
export const NEW_PROFESSION_ROUTE_META = [
  PROFESSION_INDEX_ROUTE_META,
  ...PROFESSION_ROUTE_META,
  ...PROFESSION_CHILD_ROUTE_META,
]
